import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Table,
    Button,
    Input,
    FormGroup,
    Label,
    Row,
    Col,
    Collapse,
} from "reactstrap";
import { AlunoContext } from "../../context/AlunoContext";
import { AuthContext } from "../../context/AuthContext";
import { LoadingContext } from "../../context/LoadingContext";
import { AlunoType } from "../../context/hooks/useAluno";
import { CursoType } from "../../context/hooks/useCurso";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import PaginationMaterial from "../PaginationMaterial/PaginationMaterial";

interface AlunosSemVinculoModalProps {
    isOpen: boolean;
    toggle: () => void;
    contratoId: number;
    cursosDoContrato: CursoType[];
    onRevinculado: () => void;
}

export const AlunosSemVinculoModal: React.FC<AlunosSemVinculoModalProps> = ({
    isOpen,
    toggle,
    contratoId,
    cursosDoContrato,
    onRevinculado,
}) => {
    const { objUserTk } = useContext(AuthContext);
    const { getAlunosSemVinculo, revinculaAluno } = useContext(AlunoContext);
    const { loading } = useContext(LoadingContext);

    const [alunosSemVinculo, setAlunosSemVinculo] = useState<AlunoType[]>([]);
    const [pageCount, setPageCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [searchName, setSearchName] = useState<string>("");
    const [searchReferenceCode, setSearchReferenceCode] = useState<string>("");
    const [selectedAlunoIdForVinculo, setSelectedAlunoIdForVinculo] = useState<number | null>(null);
    const [selectedCursoId, setSelectedCursoId] = useState<number>(0);

    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const debounceRefCodeRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (isOpen) {
            loadAlunosSemVinculo(1, "", undefined, pageSize);
        } else {
            // Reset state when modal closes
            setAlunosSemVinculo([]);
            setPageCount(0);
            setCurrentPage(1);
            setPageSize(10);
            setSearchName("");
            setSearchReferenceCode("");
            setSelectedAlunoIdForVinculo(null);
            setSelectedCursoId(0);
        }
    }, [isOpen]);

    const loadAlunosSemVinculo = async (page: number, name: string, refCode: number | undefined, size: number) => {
        const result = await getAlunosSemVinculo(page, objUserTk.EmpresaId, size, name, refCode);
        if (result) {
            setAlunosSemVinculo(result.result);
            setPageCount(result.pageCount);
            setCurrentPage(page);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchName(value);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            const refCode = searchReferenceCode ? Number(searchReferenceCode) : undefined;
            loadAlunosSemVinculo(1, value, refCode, pageSize);
        }, 300);
    };

    const handleReferenceCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchReferenceCode(value);

        if (debounceRefCodeRef.current) {
            clearTimeout(debounceRefCodeRef.current);
        }

        debounceRefCodeRef.current = setTimeout(() => {
            const refCode = value ? Number(value) : undefined;
            loadAlunosSemVinculo(1, searchName, refCode, pageSize);
        }, 300);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        const refCode = searchReferenceCode ? Number(searchReferenceCode) : undefined;
        loadAlunosSemVinculo(value, searchName, refCode, pageSize);
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        const refCode = searchReferenceCode ? Number(searchReferenceCode) : undefined;
        // Recarrega da página 1 com o novo tamanho
        setTimeout(() => {
            loadAlunosSemVinculo(1, searchName, refCode, newSize);
        }, 0);
    };

    const handleOpenVinculoForm = (alunoId: number) => {
        setSelectedAlunoIdForVinculo(alunoId);
        setSelectedCursoId(cursosDoContrato.length > 0 ? cursosDoContrato[0].id : 0);
    };

    const handleCancelVinculo = () => {
        setSelectedAlunoIdForVinculo(null);
        setSelectedCursoId(0);
    };

    const handleConfirmVinculo = async () => {
        if (!selectedAlunoIdForVinculo || selectedCursoId <= 0) {
            return;
        }

        const success = await revinculaAluno(selectedAlunoIdForVinculo, contratoId, selectedCursoId);
        
        if (success) {
            // Remove aluno da lista local
            setAlunosSemVinculo(prev => prev.filter(a => a.id !== selectedAlunoIdForVinculo));
            setSelectedAlunoIdForVinculo(null);
            setSelectedCursoId(0);
            
            // Notifica o componente pai para recarregar a lista de alunos vinculados
            onRevinculado();
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl">
            <ModalHeader toggle={toggle}>
                <i className="fa-solid fa-link-slash"></i> Alunos sem vínculo
            </ModalHeader>
            <ModalBody>
                <Row className="mb-3">
                    <Col md="4">
                        <FormGroup>
                            <Label className="form-control-label">Buscar por nome</Label>
                            <Input
                                className="form-control-alternative"
                                type="text"
                                placeholder="Digite o nome do aluno..."
                                value={searchName}
                                onChange={handleSearchChange}
                            />
                        </FormGroup>
                    </Col>
                    <Col md="4">
                        <FormGroup>
                            <Label className="form-control-label">Código de referência</Label>
                            <Input
                                className="form-control-alternative"
                                type="number"
                                placeholder="Digite o código..."
                                value={searchReferenceCode}
                                onChange={handleReferenceCodeChange}
                            />
                        </FormGroup>
                    </Col>
                    <Col md="4">
                        <FormGroup>
                            <Label className="form-control-label">Itens por página</Label>
                            <Input
                                className="form-control-alternative"
                                type="select"
                                value={pageSize}
                                onChange={handlePageSizeChange}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </Input>
                        </FormGroup>
                    </Col>
                </Row>

                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        {alunosSemVinculo.length === 0 ? (
                            <div className="text-center my-4">
                                <i className="fa-solid fa-users-slash" style={{
                                    fontSize: "3rem",
                                    color: "#adb5bd",
                                    marginBottom: "10px",
                                    display: "block"
                                }}></i>
                                <p style={{ color: "#6c757d" }}>
                                    Nenhum aluno sem vínculo encontrado.
                                </p>
                            </div>
                        ) : (
                            <Table hover responsive className="table-striped">
                                <thead>
                                    <tr>
                                        <th style={{ width: "80px" }}>Sequência</th>
                                        <th style={{ width: "120px" }}>Código Ref.</th>
                                        <th>Nome</th>
                                        <th>Email</th>
                                        <th>CPF</th>
                                        <th style={{ width: "150px", textAlign: "center" }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {alunosSemVinculo.map((aluno: AlunoType) => (
                                        <React.Fragment key={aluno.id}>
                                            <tr>
                                                <td>{aluno.id}</td>
                                                <td>{aluno.referenceCode || "-"}</td>
                                                <td>{aluno.name}</td>
                                                <td>{aluno.email}</td>
                                                <td>{aluno.cpf || "-"}</td>
                                                <td style={{ textAlign: "center" }}>
                                                    {selectedAlunoIdForVinculo === aluno.id ? (
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={handleCancelVinculo}
                                                        >
                                                            <i className="fa-solid fa-times"></i> Cancelar
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            color="success"
                                                            size="sm"
                                                            onClick={() => handleOpenVinculoForm(aluno.id)}
                                                        >
                                                            <i className="fa-solid fa-link"></i> Vincular
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                            {selectedAlunoIdForVinculo === aluno.id && (
                                                <tr>
                                                    <td colSpan={6} style={{ backgroundColor: "#f6f9fc", padding: "20px" }}>
                                                        <Collapse isOpen={true}>
                                                            <h6 style={{ 
                                                                marginBottom: "15px",
                                                                color: "#32325d",
                                                                fontWeight: "600"
                                                            }}>
                                                                Vincular aluno ao contrato
                                                            </h6>
                                                            
                                                            <div style={{
                                                                backgroundColor: "#e9ecef",
                                                                padding: "12px 15px",
                                                                borderRadius: "4px",
                                                                marginBottom: "20px"
                                                            }}>
                                                                <strong>{aluno.name}</strong>
                                                                {aluno.referenceCode && (
                                                                    <span style={{ marginLeft: "15px", color: "#666" }}>
                                                                        Código: {aluno.referenceCode}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <Row>
                                                                <Col md="8">
                                                                    <FormGroup>
                                                                        <Label className="form-control-label">
                                                                            Selecione o curso
                                                                        </Label>
                                                                        <Input
                                                                            className="form-control-alternative"
                                                                            type="select"
                                                                            value={selectedCursoId}
                                                                            onChange={(e) =>
                                                                                setSelectedCursoId(Number(e.target.value))
                                                                            }
                                                                        >
                                                                                    {cursosDoContrato.length === 0 ? (
                                                                                        <option value={0}>
                                                                                            Nenhum curso disponível
                                                                                        </option>
                                                                                    ) : (
                                                                                        cursosDoContrato.map((curso) => (
                                                                                            <option key={curso.id} value={curso.id}>
                                                                                                {curso.name}
                                                                                            </option>
                                                                                        ))
                                                                                    )}
                                                                                </Input>
                                                                    </FormGroup>
                                                                </Col>
                                                                <Col md="4" className="d-flex align-items-end">
                                                                    <FormGroup className="mb-3">
                                                                        <Button
                                                                            color="success"
                                                                            onClick={handleConfirmVinculo}
                                                                            disabled={selectedCursoId <= 0}
                                                                            className="mr-2"
                                                                        >
                                                                            <i className="fa-solid fa-check"></i> Confirmar vínculo
                                                                        </Button>
                                                                        <Button
                                                                            color="secondary"
                                                                            onClick={handleCancelVinculo}
                                                                        >
                                                                            Cancelar
                                                                        </Button>
                                                                    </FormGroup>
                                                                </Col>
                                                            </Row>
                                                        </Collapse>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </Table>
                        )}

                        {pageCount > 1 && (
                            <div className="d-flex justify-content-end align-items-center mt-3">
                                <PaginationMaterial
                                    count={pageCount}
                                    onChange={handlePageChange}
                                />
                            </div>
                        )}
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

export default AlunosSemVinculoModal;
